// Vendors
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Snackbar, Alert } from '@mui/material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Store
import { useAppDispatch, useAppSelector } from '@store/store';
import {
  postMailDocumentStart,
  resetMailUpdateDetails,
} from '@store/features/invoice/invoiceSlice';

// Components
import SendMailPopup from '@atoms/SendMailPopup';
import FormattedMessage, { useTranslation } from '@molecules/FormattedMessage';

// Utils
import { formatDate, isNotNull } from '@utils/helpers';

// Assets
import circleSuccess from '@assets/icons/circleSuccess.svg';
import messageIcon from '@assets/icons/messageIcon.svg';
import printIcon from '@assets/icons/printIcon.svg';
import receiptLogo from '@assets/images/receipt-logo.png';
import NumberFormatter from '@atoms/NumberFormat';

interface InvoiceSuccessProps {
  isPending?: boolean;
}

const InvoiceSuccess: React.FC<InvoiceSuccessProps> = ({ isPending = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const printRef = useRef<HTMLDivElement>(null);
  const { updatedEmail, sendEmailError, sendEmailFailed } = useAppSelector(
    (state) => state.invoice
  );
  const { transactionDetails } = useAppSelector((state) => state.transaction);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mailUpdateMessage, setMailUpdateMessage] = useState('');

  const NOTE = useTranslation('EMAIL_UPDATE.NOTE');
  const ERROR_MESSAGE = useTranslation('EMAIL_UPDATE.ERROR_MESSAGE');
  const SUCCESS_MESSAGE = useTranslation('EMAIL_UPDATE.SUCCESS_MESSAGE');

  useEffect(() => {
    if (transactionDetails) {
      const customerEmail = isNotNull(transactionDetails.customer?.emailAddress)
        ? (transactionDetails.customer?.emailAddress ?? '')
        : (transactionDetails.customer?.oktaUserEmail ?? '');
      dispatch(
        postMailDocumentStart({
          transactionId: transactionDetails.transactionId || '',
          email: customerEmail,
          paymentStatus: isPending ? 'INITIATED' : 'SUCCESS',
        })
      );
    }
  }, [transactionDetails]);

  useEffect(() => {
    if (updatedEmail) {
      setIsPopupOpen(false);
      setEmail('');
      setErrorMessage('');
      setIsLoading(false);
      setMailUpdateMessage(SUCCESS_MESSAGE);
      setOpen(true);
    }
  }, [updatedEmail]);

  useEffect(() => {
    if (sendEmailFailed && sendEmailError) {
      setIsLoading(false);
      setOpen(true);
      setIsPopupOpen(false);
      setMailUpdateMessage(sendEmailError);
    }
  }, [sendEmailFailed]);

  const handleDownloadPDF = async () => {
    const originalElement = printRef.current;
    if (!originalElement) return;
    const clonedElement = originalElement.cloneNode(true) as HTMLElement;

    clonedElement.querySelectorAll('.print-content').forEach((el) => {
      (el as HTMLElement).style.display = 'flex';
    });

    clonedElement.querySelectorAll('.logo-container').forEach((el) => {
      (el as HTMLElement).style.display = 'block';
    });

    const container = document.getElementById('pdf-container');
    if (!container) return;

    container.innerHTML = '';
    container.appendChild(clonedElement);

    await new Promise((res) => setTimeout(res, 50));
    const canvas = await html2canvas(clonedElement, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save('receipt.pdf');
    container.innerHTML = '';
  };

  const openPopup = () => {
    setErrorMessage('');
    setIsPopupOpen(true);
    if (transactionDetails && transactionDetails.customer) {
      const email = isNotNull(transactionDetails.customer.emailAddress)
        ? transactionDetails.customer.emailAddress
        : transactionDetails.customer.oktaUserEmail;
      setEmail(email || '');
    }
  };

  const closePopup = () => setIsPopupOpen(false);

  const sendEmail = (email: string) => {
    setIsLoading(true);
    try {
      const payload = {
        transactionId: transactionDetails?.transactionId || '',
        email: email,
        paymentStatus: isPending ? 'INITIATED' : 'SUCCESS',
      };
      dispatch(postMailDocumentStart(payload));
      setErrorMessage('');
    } catch (_error: unknown) {
      console.error(_error);
      setErrorMessage(ERROR_MESSAGE);
    }
  };

  return (
    <div className="invoice-success_wrap">
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => {
          setOpen(false);
          setMailUpdateMessage('');
          dispatch(resetMailUpdateDetails());
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={sendEmailFailed ? 'error' : 'success'}
          variant="filled"
          onClose={() => {
            setOpen(false);
            setMailUpdateMessage('');
            dispatch(resetMailUpdateDetails());
          }}
        >
          {mailUpdateMessage}
        </Alert>
      </Snackbar>
      {transactionDetails && (
        <div ref={printRef}>
          <div className="logo-container">
            <div className="logo">
              <img src={receiptLogo} width={199} height={'auto'} />
            </div>
          </div>
          <div className="title_wrap">
            <div>
              <img src={circleSuccess} alt="success-img" />
              <Typography className="title">
                <FormattedMessage
                  id={
                    isPending
                      ? 'SUCCESS_SCREEN.PENDING_MESSAGE'
                      : 'SUCCESS_SCREEN.SUCCESS_MESSAGE'
                  }
                />
              </Typography>
            </div>
            <Typography className="confirmation-label">
              <FormattedMessage id="SUCCESS_SCREEN.LABEL.CONFIRMATION" />
            </Typography>
            <Typography className="confirmation-number">
              {transactionDetails.transactionId}
            </Typography>
          </div>

          <div className="invoice-container">
            <div className="invoice-details">
              <div className="detail-row">
                <Typography className="label">
                  <FormattedMessage id="SUCCESS_SCREEN.LABEL.CUST_ID" />
                </Typography>
                <Typography className="value">{transactionDetails.customerNumber}</Typography>
              </div>
              <div className="detail-row">
                <Typography className="label">
                  <FormattedMessage id="SUCCESS_SCREEN.LABEL.OWNER" />
                </Typography>
                <Typography className="value">
                  <span>{transactionDetails.customer?.firstName}</span>{' '}
                  <span>{transactionDetails.customer?.lastName}</span>
                  <br />
                  <span>{transactionDetails.customer?.address?.address1}</span>{' '}
                  <span>{transactionDetails.customer?.address?.address2}</span>
                  <br />
                  <span>
                    {transactionDetails.customer?.address?.city},{' '}
                    {transactionDetails.customer?.address?.state}{' '}
                    {transactionDetails.customer?.address?.zipcode}
                  </span>
                </Typography>
              </div>
              <div className="detail-row">
                <Typography className="label">
                  <FormattedMessage id="SUCCESS_SCREEN.LABEL.DATE" />
                </Typography>
                <Typography className="value">
                  {formatDate(
                    transactionDetails.transactionDate &&
                      isNotNull(transactionDetails.transactionDate)
                      ? transactionDetails.transactionDate.split('T')[0] + 'T00:00:00'
                      : ''
                  )}
                </Typography>
              </div>
              <div className="amount-row">
                <Typography className="amount-label">
                  <FormattedMessage id="SUCCESS_SCREEN.LABEL.AMOUNT" />
                </Typography>
                <Typography className="amount">
                  {transactionDetails.amount && (
                    <NumberFormatter amount={transactionDetails.amount} />
                  )}
                </Typography>
              </div>
            </div>
          </div>
          <div className="print-content">
            <Typography className="note">
              Thank you for your payment. The transaction has successfully processed and is
              complete. This company operates in partnership with Assurant, Inc.
              <p style={{ margin: '0 0' }}>
                For questions, please contact the AUSPL Insurance Team at{' '}
                <a
                  href="tel:8776428775"
                  style={{ color: 'rgb(102, 102, 102)', textDecoration: 'underline' }}
                >
                  1-877-642-8775
                </a>
                .
              </p>
            </Typography>
            <Typography className="footer">
              <p style={{ margin: '0 0' }}>©2025 Assurant, Inc. All rights reserved.</p>
              <p style={{ margin: '0 0' }}>260 Interstate North Circle, SE</p>
              <p style={{ margin: '0 0' }}>Atlanta, GA 30339-2210</p>
            </Typography>
          </div>
        </div>
      )}
      <div className="button-group">
        <button className="btn" onClick={() => void handleDownloadPDF()}>
          <img src={printIcon} alt="print-icon" />
          <Typography className="btn-label">
            <FormattedMessage id="SUCCESS_SCREEN.BUTTON.PRINT_RECEIPT" />
          </Typography>
        </button>
        <button className="btn" onClick={openPopup}>
          <img src={messageIcon} alt="message-icon" className="message-icon" />
          <Typography className="btn-label">
            <FormattedMessage id="SUCCESS_SCREEN.BUTTON.EMAIL_RECEIPT" />
          </Typography>
        </button>
        <button className="btn primary" onClick={() => void navigate('/invoices')}>
          <Typography className="btn-primary-label">
            <FormattedMessage id="SUCCESS_SCREEN.BUTTON.BACK_TO_INVOICES" />
          </Typography>
        </button>
      </div>

      {isPopupOpen && (
        <SendMailPopup
          isOpen={isPopupOpen}
          onClose={closePopup}
          label={NOTE}
          sendEmail={(email) => sendEmail(email)}
          errorMessage={errorMessage}
          setEmail={setEmail}
          email={email}
          isLoading={isLoading}
        />
      )}
      <div id="pdf-container" style={{ position: 'absolute', left: '-9999px', top: 0 }} />
    </div>
  );
};

export default InvoiceSuccess;
